import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SelectItem } from 'primeng';

import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IInboxJob } from '../interfaces/inbox-job';
import { IInboxJobFilter } from '../interfaces/inbox-job-filter';
import { IOrderInfo } from '../interfaces/task-info';
import { EditService } from './edit.service';
import { TaskService } from './task.service';
import { PostcodePipe } from '../pipes/postcode.pipe';
import { ITransactionLines } from '../interfaces/transaction-lines/transaction-lines';

@Injectable({ providedIn: 'root' })
export class InboxJobsService extends EditService<IInboxJob, IInboxJobFilter> {
  url = '/api/inboxjobs';
  entityTitle = 'InboxJobs';

  constructor(
    public http: HttpClient,
    private taskService: TaskService,
    private postcodePipe: PostcodePipe,
    handlersFactory: HttpResponsesHandlersFactory,
  ) {
    super(http, handlersFactory);
  }

  public getSingle(jobId: number): Promise<IInboxJob> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/single/${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getJob(jobId: number, orderId: number = null): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/getJob?id=${jobId}&isCustomerInbox=${orderId != null}`),
      this.handlersFactory.getForReadAll(),
    ).then((data) => {
      //convert all job orders' tasks into common list of order
      let o: IOrderInfo;
      for (let i = 0; i < data.orders.length; i++) {
        if (!orderId || data.orders[i].id == orderId) {
          if (o) o.tasks = o.tasks.concat(data.orders[i].tasks);
          else o = data.orders[i];

          o.jobNumber = data.jobNumber;
          o.jobId = jobId;
          o.existingCapacityPrice = data.existingCapacityPrice;
          o.supplierPrice = data.supplierPrice;
        }
      }

      return o;
    });
  }

  public bindTaskTypes(): Promise<SelectItem[]> {
    return this.taskService.GetTaskTypes().then((res) => {
      var l = [this.taskTypeDefSelect];
      if (res != null) {
        res.forEach((item) => {
          l.push({ label: item.name, value: item.id });
        });
      }
      return l;
    });
  }

  public bindTaskTypesWithoutDefault(): Promise<SelectItem[]> {
    return this.taskService.GetTaskTypes().then((res) => {
      var l = [];
      if (res != null) {
        res.forEach((item) => {
          l.push({ label: item.name, value: item.id });
        });
      }
      return l;
    });
  }

  public markJobAsClicked(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/markJobAsClicked?id=${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  //--- GUI
  public addressStr(addr: any) {
    var addrStr = new Array();
    if (addr == null) return '';

    if (addr.businessName) {
      addrStr.push(addr.businessName);
    }
    if (addr.postcode) {
      addrStr.push(this.postcodePipe.transform(addr.postcode));
    }
    if (addr.addressLine1) {
      addrStr.push(addr.addressLine1);
    }
    if (addr.city) {
      addrStr.push(addr.city);
    }
    return addrStr.join(', ');
  }

  public taskTypeDefSelect: SelectItem = { label: 'All Movement Types', value: null };
  public eregionDefSelect: SelectItem = { label: 'All Areas', value: null };
  public eareaDefSelect: SelectItem = { label: 'All Regions', value: null };
  public taskStepDefSelect: SelectItem[] = [
    { label: 'All Step Types', value: null },
    { label: 'Start Task', value: 0 },
    { label: 'Confirm Insurance', value: 9 },
    { label: 'Pickup Photo', value: 1 },
    { label: 'Pickup Signature', value: 2 },
    { label: 'DropOff Photo', value: 3 },
    { label: 'DropOff Signature', value: 4 },
    { label: 'Start Movement', value: 5 },
    { label: 'End Movement', value: 6 },
    { label: 'Rating', value: 7 },
    { label: 'Complete Task', value: 8 },
    { label: 'Paused', value: 13 },
  ];

  //--- /GUI

  getOrderPriceDetails(orderId: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/getOrderPriceDetails/${orderId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getJobPriceDetails(jobId: number): Promise<ITransactionLines[]> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/getJobPriceDetails/${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  savePriceDetails(item) {
    return this.handleRequest(
      this.http.post<any>(`${this.url}/savePriceDetails`, item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  getHiddenWatchingCounts(id: number, fetchParameters: any): Promise<any> {
    let params = this.constructHttpParams(fetchParameters);

    return this.http
      .get<any>(`${this.url}/getSupplierJobsWatchedHiddenCount`, { params: params })
      .toPromise();
  }
}
